/**
 * Value ledger — the honest tally of work the agents produced and a human
 * approved.
 *
 * This is deliberately small and pure: it reads the approvals collection and
 * answers one question Argo (as customer-zero) and any future customer will
 * ask — "what did the agents actually do for me, and what was it worth?"
 *
 * It is the seed of outcome-metering (ROADMAP-TO-6M.md §7): the same ledger
 * that gates trust (human approval) becomes the thing we can bill on, because
 * every counted unit is a real, approved, logged action.
 *
 * Three honesty rules are baked in (and locked by valueLedger.test.ts):
 *   1. Only `status === 'approved'` items count. A pending or dismissed
 *      proposal is intent, not delivered work — it must never inflate the
 *      number.
 *   2. Dollars come *only* from actions that genuinely carry a USD figure
 *      (create_person / create_document `value`). We call it "influenced",
 *      never "recovered": the approved action touched that amount, it did not
 *      bank cash.
 *   3. Minutes-saved are transparent, conservative per-kind estimates of the
 *      operator time a draft-and-propose saves a human who now only reviews
 *      and approves. The constants live here, exported, so the assumption is
 *      auditable rather than a black box.
 */

import type { AgentAction } from './types';
import type { ActionOutcome, ActionOutcomeStatus, ApprovalItem } from '../db/types';

/**
 * Operator minutes saved per approved action, by kind.
 *
 * These are *estimates* — the human time it would take to do the same work by
 * hand (data entry, drafting, triage) minus the few seconds it takes to review
 * and approve. Deliberately conservative so the headline never overstates.
 * Exported so the assumption is visible and testable, not buried.
 */
export const MINUTES_SAVED_BY_KIND: Record<AgentAction['kind'], number> = {
  create_person: 5, // entering a contact from a lead by hand
  set_prospect_stage: 1,
  set_customer_status: 1,
  create_document: 20, // drafting a proposal/contract from scratch
  move_workflow_stage: 1,
  create_ticket: 5,
  set_ticket_status: 1,
  assign_ticket: 2, // the triage/routing judgement call
  create_task: 3,
  schedule_follow_up: 3,
  log_activity: 2,
  update_brain: 2, // capturing a sourced account fact by hand
  update_record_field: 1, // one governed inline cell edit
  send_customer_update: 12, // composing + sending a customer status email by hand
  send_drafted_email: 10, // composing a reminder/follow-up/check-in email by hand
  send_sequence_step: 3, // drafting + personalizing + sending one cadence email
  mark_enrollment_replied: 1, // logging a reply + stopping the sequence
  publish_content: 5, // manually cross-posting a scored draft to LinkedIn/X
};

/** Per-kind rollup inside a {@link ValueSummary}. */
export interface ValueByKind {
  count: number;
  minutesSaved: number;
  dollarsInfluenced: number;
}

/** The honest summary of approved, delivered agent work. */
export interface ValueSummary {
  /** Number of approved actions (delivered work, not proposals). */
  approvedCount: number;
  /** Total estimated operator minutes saved. */
  minutesSaved: number;
  /** `minutesSaved` expressed in hours, rounded to one decimal. */
  hoursSaved: number;
  /** USD touched by approved actions that carry a value. Influenced, not banked. */
  dollarsInfluenced: number;
  /** Breakdown keyed by action kind (only kinds that actually occurred). */
  byKind: Partial<Record<AgentAction['kind'], ValueByKind>>;
}

/** Dollar figure an action genuinely carries, or 0 if it carries none. */
function dollarsOf(action: AgentAction): number {
  // create_person / create_document carry a deal/contract `value`; create_task
  // carries the at-risk amount a revenue action chases (past-due invoice, ARR
  // on a renewal). All three are dollars the approved action touched.
  if (
    action.kind === 'create_person' ||
    action.kind === 'create_document' ||
    action.kind === 'create_task'
  ) {
    return action.value ?? 0;
  }
  return 0;
}

/**
 * Summarize the value of delivered (approved) agent work from the approvals
 * ledger. Pending and dismissed items are ignored by construction.
 */
export function summarizeValueDelivered(approvals: ApprovalItem[]): ValueSummary {
  const byKind: Partial<Record<AgentAction['kind'], ValueByKind>> = {};
  let approvedCount = 0;
  let minutesSaved = 0;
  let dollarsInfluenced = 0;

  for (const item of approvals) {
    if (item.status !== 'approved') continue;
    const action = item.action as AgentAction;
    const minutes = MINUTES_SAVED_BY_KIND[action.kind] ?? 0;
    const dollars = dollarsOf(action);

    approvedCount += 1;
    minutesSaved += minutes;
    dollarsInfluenced += dollars;

    const bucket = byKind[action.kind] ?? {
      count: 0,
      minutesSaved: 0,
      dollarsInfluenced: 0,
    };
    bucket.count += 1;
    bucket.minutesSaved += minutes;
    bucket.dollarsInfluenced += dollars;
    byKind[action.kind] = bucket;
  }

  return {
    approvedCount,
    minutesSaved,
    hoursSaved: Math.round((minutesSaved / 60) * 10) / 10,
    dollarsInfluenced,
    byKind,
  };
}

// ===========================================================================
// Outcome tracking — the honest bridge from "influenced" toward "proven"
// ===========================================================================
//
// Approval time tells us the agent's work was accepted; it does NOT tell us
// what happened next (did the follow-up get a reply? did the at-risk account
// renew?). These primitives let an outcome be recorded against an approved
// action and rolled up. Honesty rule #4: an outcome that was never recorded
// is 'pending' — unknown is NEVER assumed positive. Auto-detection (email /
// CRM signals) plugs in later by calling `recordOutcome`; nothing here
// changes the existing influenced tally.

/** Rollup of recorded outcomes across approved actions. */
export interface OutcomeRollup {
  /** Number of approved actions considered (the honest denominator). */
  total: number;
  /** Counts by outcome status; approved-but-unrecorded counts as 'pending'. */
  byStatus: Record<ActionOutcomeStatus, number>;
  /**
   * positive / total-approved. Deliberately uses ALL approved actions as the
   * denominator (not just the resolved ones) so unrecorded outcomes drag the
   * rate down instead of inflating it. 0 when there are no approved actions.
   */
  positiveRate: number;
}

/** {@link ValueSummary} plus the outcome breakdown. */
export interface ValueSummaryWithOutcomes extends ValueSummary {
  outcomes: OutcomeRollup;
}

/**
 * Record an outcome against an approved action. Pure: returns a new
 * ApprovalItem with the outcome stamped; the input is not mutated. The
 * caller (UI handler, future email/CRM signal listener) persists the result.
 */
export function recordOutcome(
  item: ApprovalItem,
  status: ActionOutcomeStatus,
  opts: { note?: string; recordedAt?: string } = {},
): ApprovalItem {
  const outcome: ActionOutcome = {
    status,
    recordedAt: opts.recordedAt ?? new Date().toISOString(),
  };
  if (opts.note !== undefined) outcome.note = opts.note;
  return { ...item, outcome };
}

/** Outcome status of an item — unknown (unrecorded) is honestly 'pending'. */
function outcomeStatusOf(item: ApprovalItem): ActionOutcomeStatus {
  return item.outcome?.status ?? 'pending';
}

/**
 * Roll up recorded outcomes across the approvals ledger. Only approved
 * items are considered — a pending or dismissed proposal never happened,
 * so it has no outcome to track.
 */
export function outcomeRollup(approvals: ApprovalItem[]): OutcomeRollup {
  const byStatus: Record<ActionOutcomeStatus, number> = {
    pending: 0,
    positive: 0,
    negative: 0,
    no_response: 0,
  };
  let total = 0;

  for (const item of approvals) {
    if (item.status !== 'approved') continue;
    total += 1;
    byStatus[outcomeStatusOf(item)] += 1;
  }

  return {
    total,
    byStatus,
    positiveRate: total === 0 ? 0 : byStatus.positive / total,
  };
}

/**
 * Sibling of {@link summarizeValueDelivered}: the same honest tally, plus
 * the outcome breakdown (X positive / Y pending). The base fields are
 * computed by the existing function, unchanged — code relying on the
 * influenced tally sees exactly the same numbers.
 */
export function summarizeValueWithOutcomes(
  approvals: ApprovalItem[],
): ValueSummaryWithOutcomes {
  return {
    ...summarizeValueDelivered(approvals),
    outcomes: outcomeRollup(approvals),
  };
}
